import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

/**
 * `/accounting/ledger` — saldokonto.
 *
 * Who owes whom on a given day. "As of today" is only the default: an
 * accountant asks as of the close, as of the tax return, as of the day a
 * contract was signed — and the answer has to be the state **then**, not
 * today projected backwards.
 */

const searchSchema = z.object({ asOf: z.string().optional() });

type OpenItem = {
  id: string;
  number: string | null;
  partnerName: string;
  issuedOn: string;
  dueOn: string;
  currency: string;
  total: string;
  paid: string;
  outstanding: string;
  daysOverdue: number;
};

type AgingBucket = { label: string; amount: string; count: number };
type Side = { items: OpenItem[]; total: string; aging: AgingBucket[] };

export const Route = createFileRoute("/_app/accounting/ledger")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ asOf: search.asOf }),
  loader: async ({ deps }) => {
    const query = deps.asOf ? `?asOf=${encodeURIComponent(deps.asOf)}` : "";
    try {
      return await api.get<{ asOf: string; receivables: Side; payables: Side }>(
        `/api/accounting/ledger${query}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: LedgerPage,
});

function money(amount: string, currency = "CZK"): string {
  return `${Number(amount).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function LedgerPage() {
  const { asOf, receivables, payables } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Saldokonto</h1>
          <p className="text-muted-foreground text-sm">
            As of <LocalDate value={asOf} /> — only payments received by that day count.
          </p>
        </div>
        <div>
          <label htmlFor="asOf" className="text-muted-foreground text-xs">
            K datu
          </label>
          <input
            id="asOf"
            type="date"
            defaultValue={asOf.slice(0, 10)}
            className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) =>
              navigate({
                to: "/accounting/ledger",
                search: { ...search, asOf: e.target.value || undefined },
              })
            }
          />
        </div>
      </div>

      <LedgerSide title="Receivables" description="What customers owe us" side={receivables} />
      <LedgerSide title="Payables" description="What we owe suppliers" side={payables} />
    </div>
  );
}

function LedgerSide({
  title,
  description,
  side,
}: {
  title: string;
  description: string;
  side: Side;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <div className="font-semibold text-xl">{money(side.total)}</div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-4 text-sm">
          {side.aging.map((bucket) => (
            <div key={bucket.label}>
              <div className="text-muted-foreground text-xs">{bucket.label}</div>
              <div
                className={
                  bucket.label === "Over 90 days" && Number(bucket.amount) > 0
                    ? "font-semibold text-destructive"
                    : "font-semibold"
                }
              >
                {money(bucket.amount)}
              </div>
              <div className="text-muted-foreground text-xs">{bucket.count} documents</div>
            </div>
          ))}
        </div>

        {side.items.length === 0 ? (
          <p className="text-muted-foreground text-sm">Nothing open.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-4 font-medium">Doklad</th>
                <th className="py-1 pr-4 font-medium">Partner</th>
                <th className="py-1 pr-4 font-medium">Splatnost</th>
                <th className="py-1 pr-4 text-right font-medium">Celkem</th>
                <th className="py-1 pr-4 text-right font-medium">Uhrazeno</th>
                <th className="py-1 text-right font-medium">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {side.items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="py-1 pr-4 font-mono text-xs">{item.number ?? "—"}</td>
                  <td className="py-1 pr-4">{item.partnerName}</td>
                  <td className="py-1 pr-4 text-muted-foreground">
                    <LocalDate value={item.dueOn} />
                    {item.daysOverdue > 0 && (
                      <Badge variant="destructive" className="ml-2 text-[10px]">
                        {item.daysOverdue} days
                      </Badge>
                    )}
                  </td>
                  <td className="py-1 pr-4 text-right tabular-nums">
                    {money(item.total, item.currency)}
                  </td>
                  <td className="py-1 pr-4 text-right tabular-nums text-muted-foreground">
                    {money(item.paid, item.currency)}
                  </td>
                  <td className="py-1 text-right font-medium tabular-nums">
                    {money(item.outstanding, item.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
